/**
 * 설비 템플릿 캐시 관리
 * Feature 288: 오프라인 설비 문서 생성 지원
 */

import { STORES, openDB, putRecord, getRecord, getAllRecords, clearStore } from "./index";
import type { CachedMachineTemplate } from "./types";
import { CHECK_RESULTS } from "@/types";

/**
 * 설비 템플릿 저장 (upsert)
 * @param template 캐시할 템플릿 데이터
 */
export async function saveMachineTemplate(template: CachedMachineTemplate): Promise<void> {
  await putRecord(STORES.MACHINE_TEMPLATES, template);
}

/**
 * 여러 설비 템플릿 일괄 저장 (upsert)
 * 단일 트랜잭션으로 일괄 처리하여 성능 최적화
 * @param templates 캐시할 템플릿 배열
 */
export async function saveMachineTemplates(templates: CachedMachineTemplate[]): Promise<void> {
  if (templates.length === 0) return;

  const db = await openDB();
  const now = Date.now();

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORES.MACHINE_TEMPLATES, "readwrite");
    const store = tx.objectStore(STORES.MACHINE_TEMPLATES);

    for (const template of templates) {
      store.put({ ...template, cachedAt: now });
    }

    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * 특정 설비 템플릿 조회
 * @param key 설비 키 (예: "air_handling_unit")
 * @returns 캐시된 템플릿 또는 undefined
 */
export async function getMachineTemplate(key: string): Promise<CachedMachineTemplate | undefined> {
  return getRecord<CachedMachineTemplate>(STORES.MACHINE_TEMPLATES, key);
}

/**
 * 모든 설비 템플릿 조회
 * @returns 캐시된 모든 템플릿 배열
 */
export async function getAllMachineTemplates(): Promise<CachedMachineTemplate[]> {
  return getAllRecords<CachedMachineTemplate>(STORES.MACHINE_TEMPLATES);
}

/**
 * 설비 템플릿 캐시 전체 삭제
 */
export async function clearMachineTemplates(): Promise<void> {
  await clearStore(STORES.MACHINE_TEMPLATES);
}

/**
 * 템플릿 캐시가 유효한지 확인
 * @param maxAgeMs 최대 캐시 유효 시간 (기본: 24시간)
 * @returns 유효하면 true, 갱신 필요하면 false
 */
export async function isTemplateCacheValid(
  maxAgeMs: number = 24 * 60 * 60 * 1000
): Promise<boolean> {
  const templates = await getAllMachineTemplates();

  if (templates.length === 0) {
    return false;
  }

  // 가장 오래된 캐시 시점 확인
  const oldestCachedAt = Math.min(...templates.map((t) => t.cachedAt));
  const age = Date.now() - oldestCachedAt;

  return age < maxAgeMs;
}

/**
 * 템플릿 캐시 개수 조회
 * @returns 캐시된 템플릿 개수
 */
export async function getTemplateCacheCount(): Promise<number> {
  const templates = await getAllMachineTemplates();
  return templates.length;
}

// ============================================
// 오프라인 설비 문서 생성
// ============================================

import type { CachedMachine } from "./types";
import { cacheMachine, getCachedMachinesByProject } from "./machines";

/**
 * 간단한 UUID 생성 함수
 * crypto.randomUUID()가 지원되면 사용, 아니면 fallback
 */
function generateUUID(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback: 간단한 UUID v4 형식
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * 캐시된 템플릿에서 오프라인 설비 문서 생성
 * @param projectId 프로젝트 ID
 * @param projectType 프로젝트 타입 (PERFORMANCE_CHECK | MAINTENANCE)
 * @param machineKey 설비 키 (예: "air_handling_unit")
 * @param count 생성할 설비 개수
 * @returns 생성된 설비 문서 목록 또는 null (템플릿 없음)
 */
export async function createOfflineEquipmentFromTemplate(
  projectId: string,
  projectType: string,
  machineKey: string,
  count: number,
  docLabel?: string
): Promise<CachedMachine[] | null> {
  // 1. 캐시된 템플릿 조회
  const template = await getMachineTemplate(machineKey);
  if (!template) {
    return null; // 템플릿 캐시 없음
  }

  const now = Date.now();
  const createdMachines: CachedMachine[] = [];
  // 템플릿 sectionTitle을 기본 라벨로 사용
  const label = docLabel || template.sectionTitle || machineKey;

  // 같은 프로젝트의 기존 캐시에서 measureDiscus 조회 (#469)
  // 오프라인 설비 생성 시 회사별 판정 코멘트를 새 설비에도 포함
  const existingMachines = await getCachedMachinesByProject(projectId);
  const existingProject = existingMachines.find((m) => m.project?.measureDiscus)?.project;
  const measureDiscus = existingProject?.measureDiscus;
  const projectName = existingProject?.name || "";

  // 2. 개수만큼 설비 문서 생성
  for (let i = 0; i < count; i++) {
    // 고유 ID 생성 (오프라인 생성임을 표시하기 위해 접두사 사용)
    const machineId = `offline-${generateUUID()}`;

    // 점검항목을 CheckListItem 형식으로 변환
    const checkList = template.checkListTemplate.map((item, index) => ({
      _id: `check-${machineId}-${index}`,
      category: item.group || "", // group을 category로 매핑
      item: item.title, // title을 item으로 매핑
      result: CHECK_RESULTS.OK, // 기본값 ○ (양호)
      image: "",
      imageO: "",
      imageX: "",
      underReason: "",
      underSolution: "",
    }));

    // 유지관리 항목 변환
    const maintenance = template.maintenanceTemplate.map((item, index) => ({
      _id: `mt-${machineId}-${index}`,
      category: item.group || "", // group을 category로 매핑
      item: item.title, // title을 item으로 매핑
      result: CHECK_RESULTS.OK, // 기본값 ○ (양호)
      image: "",
      imageO: "",
      imageX: "",
      underReason: "",
      underSolution: "",
    }));

    // 데이터 항목 변환
    const dataList = template.dataTemplate.map((item, index) => ({
      _id: `data-${machineId}-${index}`,
      category: item.section, // section을 category로 매핑
      item: item.title, // title을 item으로 매핑
      unit: item.unit || "",
      value: "", // 초기값 빈 문자열
    }));

    // CachedMachine 객체 생성
    const cachedMachine: CachedMachine = {
      _id: `${projectId}-${machineKey}-${machineId}`,
      projectId,
      project: {
        id: projectId,
        name: projectName,
        type: projectType,
        measureDiscus,
      },
      group: {
        _id: machineKey,
        docKey: machineKey,
        docLabel: label,
      },
      machine: {
        _id: machineId,
        machineId: machineId,
        name: `${label} ${i + 1}`, // 예: "공조기 1", "공조기 2"
        model: "", // modelNo → 캐시 필드명은 model
        manufacturer: "", // modelCreator → 캐시 필드명은 manufacturer
      },
      checkList,
      maintenance,
      dataList,
      pictures: [],
      synced: false,
      syncStatus: "pending",
      cachedAt: now,
      localUpdatedAt: now,
      serverVersion: 0,
    };

    // IndexedDB에 저장
    await cacheMachine(cachedMachine);
    createdMachines.push(cachedMachine);
  }

  return createdMachines;
}
